import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { type KioskDisplayProductList } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";
import {
  type BasketDiscount,
  type CapturedWeight
} from "~/stores/kiosk-session/types";

type ProductDetailState = {
  weight: CapturedWeight | null;
};

type ProductDetailAction = {
  type: "SET_PRODUCT_WEIGHT";
  payload: CapturedWeight;
};

function reducer(
  state: ProductDetailState,
  action: ProductDetailAction
): ProductDetailState {
  switch (action.type) {
    case "SET_PRODUCT_WEIGHT":
      return { ...state, weight: action.payload };
    default:
      return state;
  }
}

type ProductDetailContextValue = {
  item: KioskDisplayProductList;
  discountId: string | null;
  stampCardDiscount: BasketDiscount | undefined;
  weight: CapturedWeight | null;
  close: () => void;
  setWeight: (weight: CapturedWeight) => void;
};

const ProductDetailContext = createContext<ProductDetailContextValue | null>(
  null
);

type UseProductDetailDataResult =
  | { status: "pending"; item: null }
  | { status: "error"; item: null }
  | {
      status: "success";
      item: KioskDisplayProductList;
      stampCardDiscount: BasketDiscount | undefined;
    };

export function useProductDetailData(): UseProductDetailDataResult {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const discountId = searchParams.get("discount");

  const { productsList, productsRedeemable, status } = useKioskProducts();

  if (status === "pending") {
    return { status: "pending", item: null };
  }

  if (status === "error") {
    return { status: "error", item: null };
  }

  const item = discountId
    ? productsRedeemable.find(
        (item) =>
          item.product.id === productId &&
          item.discounts.some((discount) => discount.id === discountId)
      )
    : productsList.find((item) => item.product.id === productId);

  if (!item) {
    return { status: "error", item: null };
  }

  const stampCardDiscount = item.discounts.find(
    (discount) => discount.id === discountId
  );

  return { status: "success", item, stampCardDiscount };
}

type ProductDetailProviderProps = PropsWithChildren<{
  item: KioskDisplayProductList;
  stampCardDiscount: BasketDiscount | undefined;
}>;

export const ProductDetailProvider = ({
  children,
  item,
  stampCardDiscount
}: ProductDetailProviderProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const discountId = searchParams.get("discount");

  const [state, dispatch] = useReducer(reducer, { weight: null });

  const close = useCallback(() => {
    navigate("/kiosk/products");
  }, [navigate]);

  const setWeight = useCallback((weight: CapturedWeight) => {
    dispatch({ type: "SET_PRODUCT_WEIGHT", payload: weight });
  }, []);

  const value = useMemo(
    (): ProductDetailContextValue => ({
      item,
      discountId,
      stampCardDiscount,
      weight: state.weight,
      close,
      setWeight
    }),
    [item, discountId, stampCardDiscount, state.weight, close, setWeight]
  );

  return (
    <ProductDetailContext.Provider value={value}>
      {children}
    </ProductDetailContext.Provider>
  );
};

export function useProductDetail() {
  const ctx = useContext(ProductDetailContext);
  if (!ctx) {
    throw new Error(
      "useProductDetail must be used inside ProductDetailProvider"
    );
  }
  return ctx;
}
